import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Star, MapPin, Plus, Settings, QrCode, 
  Building2, Heart, ExternalLink,
  ChevronDown, ChevronUp, Copy, Check, Zap, TrendingUp, Crown,
  BarChart3, CreditCard, LogOut, IndianRupee,
  Eye, AlertTriangle, Loader2, Download, Target, Users, Sparkles, Mic, Pencil
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Load Razorpay script dynamically
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const PLAN_INFO = {
  BASIC: { name: 'Starter', limit: null, icon: Zap, color: 'teal', price: '₹999/mo' },
  GROWTH: { name: 'Growth', limit: null, icon: TrendingUp, color: 'coral', price: '₹1,999/mo' },
  PRO: { name: 'Enterprise', limit: null, icon: Crown, color: 'warning', price: 'Custom' }
};

const TOP_UP_CREDITS = 50;
const TOP_UP_PRICE = 499;

export const ClientDashboard = () => {
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedLink, setCopiedLink] = useState(false);
  const [showTopUpDialog, setShowTopUpDialog] = useState(false);
  const [processingTopUp, setProcessingTopUp] = useState(false);
  const [showServicesExpanded, setShowServicesExpanded] = useState(true);
  const [addingService, setAddingService] = useState(false);
  const [editingService, setEditingService] = useState(null); // Service being edited
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState(null);

  // New service form
  const [newService, setNewService] = useState({
    service_name: '',
    service_category: '',
    problem_it_solves: '',
    outcome_benefit: '',
    recommended_for: '',
    staff_name: '',
    staff_role: '',
    keywords: '',
    locality: '',
    city: '',
    // Tone field
    review_tone: 'friendly',
    // SEO fields
    seo_keywords: '',
    target_audience: '',
    unique_selling_point: '',
  });

  // Settings form
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({
    google_review_link: '',
    clinic_speciality: '',
    city: ''
  });

  const clientId = localStorage.getItem('client_id');
  const clinicName = localStorage.getItem('clinic_name');
  const userName = localStorage.getItem('user_name');

  // Download QR code
  const downloadQrCode = async () => {
    try {
      const response = await fetch(`${API}/client/${clientId}/qrcode`);
      if (!response.ok) throw new Error('Failed to download QR code');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${clinicName || 'clinic'}_qr_code.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('QR code downloaded!');
    } catch (err) {
      toast.error('Failed to download QR code');
    }
  };

  // Load QR code preview
  const loadQrCodePreview = useCallback(async () => {
    if (!clientId) return;
    try {
      const response = await fetch(`${API}/client/${clientId}/qrcode`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        setQrCodeUrl(url);
      }
    } catch (err) {
      console.error('Failed to load QR code preview:', err);
    }
  }, [clientId]);

  // Load payment config
  const loadPaymentConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API}/payment/config`);
      if (res.ok) {
        const config = await res.json();
        setPaymentConfig(config);
      }
    } catch (err) {
      console.error('Failed to load payment config:', err);
    }
  }, []);

  useEffect(() => {
    if (!clientId) {
      navigate('/login');
      return;
    }
    fetchClientData();
    loadQrCodePreview();
    loadPaymentConfig();
  }, [clientId, navigate, loadQrCodePreview, loadPaymentConfig]);

  const fetchClientData = async () => {
    try {
      const [clientRes, usageRes] = await Promise.all([
        fetch(`${API}/client/${clientId}`),
        fetch(`${API}/client/${clientId}/usage`)
      ]);
      
      if (!clientRes.ok || !usageRes.ok) {
        throw new Error('Failed to load data');
      }
      
      const clientData = await clientRes.json();
      const usageData = await usageRes.json();
      
      setClient(clientData);
      setUsage(usageData);
      setEditData({
        google_review_link: clientData.google_review_link || '',
        clinic_speciality: clientData.clinic_speciality || '',
        city: clientData.city || ''
      });
    } catch (err) {
      toast.error('Failed to load dashboard');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('session_token');
    localStorage.removeItem('user_role');
    localStorage.removeItem('user_name');
    localStorage.removeItem('client_id');
    localStorage.removeItem('clinic_name');
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const getReviewPageUrl = () => {
    return `${window.location.origin}/review/${clientId}`;
  };

  const copyLink = async () => {
    const url = getReviewPageUrl();
    await navigator.clipboard.writeText(url);
    setCopiedLink(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handlePurchaseTopUp = async () => {
    // Check if Razorpay is enabled
    if (!paymentConfig?.razorpay_enabled) {
      // Fallback to direct purchase (for testing)
      setProcessingTopUp(true);
      try {
        const res = await fetch(`${API}/client/${clientId}/topup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ client_id: clientId, credits: TOP_UP_CREDITS }),
        });
        
        if (!res.ok) throw new Error('Purchase failed');
        
        toast.success(`Added ${TOP_UP_CREDITS} review credits!`);
        setShowTopUpDialog(false);
        fetchClientData();
      } catch (err) {
        toast.error('Failed to process purchase');
      } finally {
        setProcessingTopUp(false);
      }
      return;
    }

    // Razorpay payment flow
    setProcessingTopUp(true);
    try {
      // Step 1: Create order
      const orderRes = await fetch(`${API}/payment/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: clientId, credits: TOP_UP_CREDITS }),
      });
      
      if (!orderRes.ok) {
        let errorMsg = 'Failed to create payment order';
        try {
          const err = await orderRes.json();
          errorMsg = err.detail || errorMsg;
        } catch {
          // Response body already read or not JSON
        }
        throw new Error(errorMsg);
      }
      
      const orderData = await orderRes.json();
      
      // Step 2: Open Razorpay checkout
      const options = {
        key: paymentConfig.key_id,
        amount: orderData.amount_paise,
        currency: orderData.currency,
        name: 'Socialxia Review',
        description: `${TOP_UP_CREDITS} Review Credits`,
        order_id: orderData.order_id,
        handler: async (response) => {
          // Step 3: Verify payment
          try {
            const verifyRes = await fetch(`${API}/payment/verify`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                client_id: clientId,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                credits: TOP_UP_CREDITS,
              }),
            });
            
            if (!verifyRes.ok) {
              const err = await verifyRes.json();
              throw new Error(err.detail || 'Payment verification failed');
            }
            
            toast.success(`Payment successful! Added ${TOP_UP_CREDITS} review credits.`);
            setShowTopUpDialog(false);
            fetchClientData();
          } catch (err) {
            toast.error(err.message || 'Payment verification failed');
          }
        },
        prefill: {
          name: client?.name || '',
          email: client?.email || '',
          contact: client?.phone || '',
        },
        theme: {
          color: '#10b981',
        },
        modal: {
          ondismiss: () => {
            setProcessingTopUp(false);
          },
        },
      };
      
      // Load Razorpay script and open checkout
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        throw new Error('Failed to load payment gateway');
      }
      
      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.on('payment.failed', (response) => {
        toast.error(`Payment failed: ${response.error.description}`);
        setProcessingTopUp(false);
      });
      razorpayInstance.open();
      
    } catch (err) {
      toast.error(err.message || 'Failed to initiate payment');
      setProcessingTopUp(false);
    }
  };

  const handleUpdateSettings = async () => {
    try {
      const res = await fetch(`${API}/client/${clientId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editData),
      });
      
      if (!res.ok) throw new Error('Update failed');
      
      toast.success('Settings updated');
      setEditMode(false);
      fetchClientData();
    } catch (err) {
      toast.error('Failed to update settings');
    }
  };

  const addService = async () => {
    if (!newService.service_name) {
      toast.error('Service name is required');
      return;
    }

    try {
      const serviceData = {
        ...newService,
        keywords: newService.keywords ? newService.keywords.split(',').map(k => k.trim()).filter(k => k) : null,
        seo_keywords: newService.seo_keywords ? newService.seo_keywords.split(',').map(k => k.trim()).filter(k => k) : null,
      };
      
      const res = await fetch(`${API}/client/${clientId}/service`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(serviceData),
      });
      
      if (!res.ok) throw new Error('Failed to add service');
      
      toast.success('Service added');
      resetServiceForm();
      setAddingService(false);
      fetchClientData();
    } catch (err) {
      toast.error('Failed to add service');
    }
  };

  const updateService = async () => {
    if (!newService.service_name) {
      toast.error('Service name is required');
      return;
    }

    try {
      const serviceData = {
        ...newService,
        keywords: newService.keywords ? newService.keywords.split(',').map(k => k.trim()).filter(k => k) : null,
        seo_keywords: newService.seo_keywords ? newService.seo_keywords.split(',').map(k => k.trim()).filter(k => k) : null,
      };
      
      const res = await fetch(`${API}/client/${clientId}/service/${editingService}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(serviceData),
      });
      
      if (!res.ok) throw new Error('Failed to update service');
      
      toast.success('Service updated');
      resetServiceForm();
      setEditingService(null);
      setAddingService(false);
      fetchClientData();
    } catch (err) {
      toast.error('Failed to update service');
    }
  };

  const startEditService = (service) => {
    // Pre-fill the form with existing service data
    setNewService({
      service_name: service.service_name || service.name || '',
      service_category: service.service_category || '',
      problem_it_solves: service.problem_it_solves || '',
      outcome_benefit: service.outcome_benefit || service.benefit || '',
      recommended_for: service.recommended_for || '',
      staff_name: service.staff_name || '',
      staff_role: service.staff_role || '',
      keywords: Array.isArray(service.keywords) ? service.keywords.join(', ') : (service.keywords || ''),
      locality: service.locality || '',
      city: service.city || '',
      review_tone: service.review_tone || 'friendly',
      seo_keywords: Array.isArray(service.seo_keywords) ? service.seo_keywords.join(', ') : (service.seo_keywords || ''),
      target_audience: service.target_audience || '',
      unique_selling_point: service.unique_selling_point || '',
    });
    setEditingService(service.id);
    setAddingService(true);
  };

  const resetServiceForm = () => {
    setNewService({ 
      service_name: '', service_category: '', problem_it_solves: '', 
      outcome_benefit: '', recommended_for: '', staff_name: '', 
      staff_role: '', keywords: '', locality: '', city: '',
      review_tone: 'friendly',
      seo_keywords: '', target_audience: '', unique_selling_point: ''
    });
  };

  const handleServiceModalClose = (open) => {
    if (!open) {
      resetServiceForm();
      setEditingService(null);
    }
    setAddingService(open);
  };

  const toggleService = async (serviceId, enable) => {
    try {
      const action = enable ? 'enable' : 'disable';
      const res = await fetch(`${API}/client/${clientId}/service/${serviceId}/${action}`, {
        method: 'POST',
      });
      
      if (!res.ok) throw new Error('Failed to update service');
      toast.success(`Service ${enable ? 'enabled' : 'disabled'}`);
      fetchClientData();
    } catch (err) {
      toast.error('Failed to update service');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card variant="glass" className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-destructive" />
            </div>
            <h2 className="font-heading text-xl font-bold text-foreground mb-2">
              Unable to load account
            </h2>
            <p className="text-muted-foreground mb-4">
              Please try logging in again.
            </p>
            <Button variant="premium" onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const planInfo = PLAN_INFO[client.plan || 'BASIC'];
  const PlanIcon = planInfo?.icon || Zap;
  const usagePercent = usage?.unlimited ? 0 : ((usage?.current_usage || 0) / (usage?.limit || 1)) * 100;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="w-full border-b border-border bg-card/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary-glow flex items-center justify-center shadow-glow">
                  <Star className="w-5 h-5 text-primary-foreground" />
                </div>
              </div>
              <div>
                <h1 className="font-heading text-lg font-bold text-foreground">Socialxia Review</h1>
                <p className="text-xs text-muted-foreground">Client Dashboard</p>
              </div>
            </Link>

            <div className="flex items-center gap-3">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-foreground">{userName || client.name}</p>
                <p className="text-xs text-muted-foreground">{clinicName || client.clinic_name}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Clinic Info Card */}
        <Card variant="elevated" className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Building2 className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl">{client.clinic_name}</CardTitle>
                  <CardDescription className="flex items-center gap-2 mt-1">
                    {client.city && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {client.city}
                      </span>
                    )}
                    {client.clinic_speciality && (
                      <>
                        <span>•</span>
                        <span>{client.clinic_speciality}</span>
                      </>
                    )}
                  </CardDescription>
                </div>
              </div>
              <Badge variant={planInfo?.color || 'teal'} className="flex items-center gap-1">
                <PlanIcon className="w-3.5 h-3.5" />
                {planInfo?.name} Plan
              </Badge>
            </div>
          </CardHeader>
        </Card>

        {/* Usage Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Monthly Usage */}
          <Card variant="glass">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Monthly Usage</p>
                  <p className="text-2xl font-bold text-foreground" data-testid="usage-count">
                    {usage?.current_usage || 0}
                    <span className="text-lg text-muted-foreground font-normal">
                      {usage?.unlimited ? '' : `/${usage?.limit}`}
                    </span>
                  </p>
                </div>
              </div>
              {!usage?.unlimited && (
                <Progress 
                  value={Math.min(usagePercent, 100)} 
                  className={`h-2 ${usage?.exceeded ? 'bg-destructive/20' : ''}`}
                />
              )}
              {usage?.unlimited && (
                <Badge variant="warning" className="mt-2">
                  <Crown className="w-3 h-3 mr-1" />
                  Unlimited
                </Badge>
              )}
            </CardContent>
          </Card>

          {/* Plan Limit */}
          <Card variant="glass">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center">
                  <PlanIcon className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Plan Limit</p>
                  <p className="text-2xl font-bold text-foreground" data-testid="plan-limit">
                    {usage?.unlimited ? '∞' : usage?.limit || 50}
                    <span className="text-lg text-muted-foreground font-normal"> /month</span>
                  </p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Resets: {usage?.reset_date || 'Next month'}
              </p>
            </CardContent>
          </Card>

          {/* Top-Up Credits */}
          <Card variant="glass">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-success" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Top-Up Credits</p>
                    <p className="text-2xl font-bold text-foreground" data-testid="topup-credits">
                      +{client.top_up_credits || 0}
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Roll-over credits (never expire)
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Buy Top-Up CTA - Hidden until Razorpay is enabled */}
        {paymentConfig?.razorpay_enabled && (
        <Card variant="elevated" className="mb-6 bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-primary-glow flex items-center justify-center">
                  <IndianRupee className="w-7 h-7 text-primary-foreground" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-foreground text-lg">Need More Reviews?</h3>
                  <p className="text-muted-foreground">Get {TOP_UP_CREDITS} extra reviews for ₹{TOP_UP_PRICE}</p>
                </div>
              </div>
              <Dialog open={showTopUpDialog} onOpenChange={setShowTopUpDialog}>
                <DialogTrigger asChild>
                  <Button variant="premium" size="lg" data-testid="buy-topup-btn">
                    <CreditCard className="w-5 h-5" />
                    Buy Top-Up
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Purchase Top-Up Credits</DialogTitle>
                    <DialogDescription>
                      Add {TOP_UP_CREDITS} review credits to your account. These credits never expire.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="py-6">
                    <div className="bg-muted/50 rounded-xl p-6 text-center">
                      <p className="text-4xl font-bold text-foreground mb-2">₹{TOP_UP_PRICE}</p>
                      <p className="text-muted-foreground">for {TOP_UP_CREDITS} reviews</p>
                      <Badge variant="success" className="mt-3">Best Value</Badge>
                    </div>
                    <div className="mt-4 space-y-2 text-sm text-muted-foreground">
                      <p className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-success" />
                        Credits never expire
                      </p>
                      <p className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-success" />
                        Use after monthly limit
                      </p>
                      <p className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-success" />
                        Instant activation
                      </p>
                    </div>
                    <p className="mt-4 text-xs text-center text-primary">
                      Secure payment via Razorpay
                    </p>
                  </div>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button 
                      variant="premium" 
                      onClick={handlePurchaseTopUp}
                      disabled={processingTopUp}
                      data-testid="confirm-topup-btn"
                    >
                      {processingTopUp ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4" />
                          Pay ₹{TOP_UP_PRICE}
                        </>
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>
        )}

        {/* QR Code Preview & Review Link */}
        <Card variant="elevated" className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-primary" />
              Your Review Page
            </CardTitle>
            <CardDescription>
              Share this link or QR code with your customers to collect reviews
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-6">
              {/* QR Code Preview */}
              <div className="flex-shrink-0">
                <div className="bg-white p-4 rounded-xl border border-border shadow-sm">
                  {qrCodeUrl ? (
                    <img 
                      src={qrCodeUrl} 
                      alt="Review Page QR Code" 
                      className="w-40 h-40 mx-auto"
                      data-testid="qr-code-preview"
                    />
                  ) : (
                    <div className="w-40 h-40 flex items-center justify-center bg-muted rounded-lg">
                      <QrCode className="w-12 h-12 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={downloadQrCode} 
                  className="w-full mt-3"
                  data-testid="download-qr-btn"
                >
                  <Download className="w-4 h-4" />
                  Download QR Code
                </Button>
              </div>
              
              {/* Link Section */}
              <div className="flex-1 space-y-4">
                <div className="bg-muted/50 rounded-xl p-4">
                  <p className="text-xs text-muted-foreground mb-1">Review Page URL</p>
                  <p className="text-sm font-medium text-foreground break-all" data-testid="review-page-url">
                    {getReviewPageUrl()}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={copyLink} data-testid="copy-review-link-btn">
                    {copiedLink ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copiedLink ? 'Copied!' : 'Copy Link'}
                  </Button>
                  <Link to={`/review/${clientId}`} target="_blank">
                    <Button variant="secondary" size="sm" data-testid="preview-review-page-btn">
                      <Eye className="w-4 h-4" />
                      Preview Page
                    </Button>
                  </Link>
                </div>
                <p className="text-xs text-muted-foreground">
                  Print the QR code and place it at your reception desk or include it in follow-up messages to customers.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Google Review Link (ONE per client) */}
        <Card variant="elevated" className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <ExternalLink className="w-5 h-5 text-primary" />
                  Google Review Link
                </CardTitle>
                <CardDescription>
                  Your clinic&apos;s Google Maps review URL (one link per account)
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => setEditMode(!editMode)} data-testid="edit-settings-btn">
                <Settings className="w-4 h-4" />
                {editMode ? 'Cancel' : 'Edit'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {editMode ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Google Review Link *</label>
                  <Input
                    placeholder="https://g.page/r/xxx/review"
                    value={editData.google_review_link}
                    onChange={(e) => setEditData(p => ({ ...p, google_review_link: e.target.value }))}
                    data-testid="edit-google-link-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Find this in Google Business Profile → Get more reviews
                  </p>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Speciality</label>
                    <Input
                      placeholder="e.g., Cosmetic Dentistry"
                      value={editData.clinic_speciality}
                      onChange={(e) => setEditData(p => ({ ...p, clinic_speciality: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">City</label>
                    <Input
                      placeholder="e.g., Bengaluru"
                      value={editData.city}
                      onChange={(e) => setEditData(p => ({ ...p, city: e.target.value }))}
                    />
                  </div>
                </div>
                <Button variant="premium" onClick={handleUpdateSettings} data-testid="save-settings-btn">
                  <Check className="w-4 h-4" />
                  Save Changes
                </Button>
              </div>
            ) : (
              <div className="bg-muted/50 rounded-xl p-4">
                {client.google_review_link ? (
                  <a 
                    href={client.google_review_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline break-all flex items-center gap-2"
                    data-testid="google-review-link"
                  >
                    {client.google_review_link}
                    <ExternalLink className="w-4 h-4 shrink-0" />
                  </a>
                ) : (
                  <p className="text-muted-foreground">No Google Review link configured. Click Edit to add one.</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Services */}
        <Card variant="elevated">
          <CardHeader 
            className="cursor-pointer" 
            onClick={() => setShowServicesExpanded(!showServicesExpanded)}
          >
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Heart className="w-5 h-5 text-primary" />
                  Services ({client.services?.length || 0})
                </CardTitle>
                <CardDescription>
                  Services offered to customers for review generation
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Dialog open={addingService} onOpenChange={handleServiceModalClose}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setEditingService(null); resetServiceForm(); }} data-testid="add-service-btn">
                      <Plus className="w-4 h-4" />
                      Add Service
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingService ? 'Edit Service' : 'Add New Service'}</DialogTitle>
                      <DialogDescription>
                        {editingService ? 'Update service details for SEO-optimized review generation.' : 'Add service details for SEO-optimized review generation.'}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-4 py-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Service Name *</label>
                        <Input
                          placeholder="e.g., Sports Physiotherapy"
                          value={newService.service_name}
                          onChange={(e) => setNewService(p => ({ ...p, service_name: e.target.value }))}
                          data-testid="service-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Category</label>
                        <Input
                          placeholder="e.g., Physiotherapy"
                          value={newService.service_category}
                          onChange={(e) => setNewService(p => ({ ...p, service_category: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2 col-span-2">
                        <label className="text-sm font-medium">Problem It Solves</label>
                        <Input
                          placeholder="e.g., neck stiffness, lower back pain"
                          value={newService.problem_it_solves}
                          onChange={(e) => setNewService(p => ({ ...p, problem_it_solves: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2 col-span-2">
                        <label className="text-sm font-medium">Outcome / Benefit</label>
                        <Input
                          placeholder="e.g., improved mobility, reduced pain"
                          value={newService.outcome_benefit}
                          onChange={(e) => setNewService(p => ({ ...p, outcome_benefit: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Staff Name</label>
                        <Input
                          placeholder="e.g., Dr. Pooja"
                          value={newService.staff_name}
                          onChange={(e) => setNewService(p => ({ ...p, staff_name: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Staff Role</label>
                        <Input
                          placeholder="e.g., Physiotherapist"
                          value={newService.staff_role}
                          onChange={(e) => setNewService(p => ({ ...p, staff_role: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Locality</label>
                        <Input
                          placeholder="e.g., HSR Layout"
                          value={newService.locality}
                          onChange={(e) => setNewService(p => ({ ...p, locality: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">City</label>
                        <Input
                          placeholder="e.g., Bengaluru"
                          value={newService.city}
                          onChange={(e) => setNewService(p => ({ ...p, city: e.target.value }))}
                        />
                      </div>
                      
                      {/* Tone of Voice */}
                      <div className="space-y-2 col-span-2">
                        <label className="text-sm font-medium flex items-center gap-2">
                          <Mic className="w-4 h-4 text-muted-foreground" />
                          Preferred Review Tone
                        </label>
                        <Select 
                          value={newService.review_tone} 
                          onValueChange={(value) => setNewService(p => ({ ...p, review_tone: value }))}
                        >
                          <SelectTrigger data-testid="tone-select">
                            <SelectValue placeholder="Select tone" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="professional">Professional</SelectItem>
                            <SelectItem value="friendly">Friendly / Casual</SelectItem>
                            <SelectItem value="enthusiastic">Enthusiastic</SelectItem>
                            <SelectItem value="short">Short & Punchy</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">How should the AI write the review?</p>
                      </div>
                      
                      {/* SEO Fields Section - Plan Gated */}
                      <div className="col-span-2 border-t border-border pt-4 mt-2">
                        <p className="text-sm font-medium text-primary flex items-center gap-2 mb-3">
                          <Target className="w-4 h-4" />
                          SEO Optimization Fields
                          {client.plan === 'BASIC' && (
                            <Badge variant="warning" className="ml-2 text-xs">Growth Plan</Badge>
                          )}
                        </p>
                      </div>
                      
                      {client.plan === 'BASIC' ? (
                        <div className="col-span-2 p-4 rounded-lg bg-warning/10 border border-warning/20">
                          <div className="flex items-start gap-3">
                            <Crown className="w-5 h-5 text-warning shrink-0 mt-0.5" />
                            <div>
                              <p className="font-medium text-foreground text-sm">Upgrade to Growth to unlock SEO Tools</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                Get access to SEO keywords, target audience targeting, and unique selling point highlighting to rank higher on Google Maps.
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="space-y-2 col-span-2">
                            <label className="text-sm font-medium flex items-center gap-2">
                              <Sparkles className="w-4 h-4 text-muted-foreground" />
                              SEO Focus Keywords
                            </label>
                            <Input
                              placeholder="e.g., best physiotherapy, back pain treatment, sports injury (comma-separated)"
                              value={newService.seo_keywords}
                              onChange={(e) => setNewService(p => ({ ...p, seo_keywords: e.target.value }))}
                              data-testid="seo-keywords-input"
                            />
                            <p className="text-xs text-muted-foreground">Keywords to naturally include in reviews for better SEO</p>
                          </div>
                          <div className="space-y-2 col-span-2">
                            <label className="text-sm font-medium flex items-center gap-2">
                              <Users className="w-4 h-4 text-muted-foreground" />
                              Target Audience
                            </label>
                            <Input
                              placeholder="e.g., working professionals, senior citizens, athletes"
                              value={newService.target_audience}
                              onChange={(e) => setNewService(p => ({ ...p, target_audience: e.target.value }))}
                              data-testid="target-audience-input"
                            />
                            <p className="text-xs text-muted-foreground">AI will write reviews from this audience perspective</p>
                          </div>
                          <div className="space-y-2 col-span-2">
                            <label className="text-sm font-medium flex items-center gap-2">
                              <Star className="w-4 h-4 text-muted-foreground" />
                              Unique Selling Point
                            </label>
                            <Input
                              placeholder="e.g., only clinic with German machines, 15+ years experience"
                              value={newService.unique_selling_point}
                              onChange={(e) => setNewService(p => ({ ...p, unique_selling_point: e.target.value }))}
                              data-testid="usp-input"
                            />
                            <p className="text-xs text-muted-foreground">What makes your service stand out</p>
                          </div>
                        </>
                      )}
                    </div>
                    <DialogFooter>
                      <DialogClose asChild>
                        <Button variant="outline">Cancel</Button>
                      </DialogClose>
                      <Button 
                        variant="premium" 
                        onClick={editingService ? updateService : addService} 
                        data-testid={editingService ? "confirm-update-service-btn" : "confirm-add-service-btn"}
                      >
                        {editingService ? 'Update Service' : 'Add Service'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                {showServicesExpanded ? (
                  <ChevronUp className="w-5 h-5 text-muted-foreground" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-muted-foreground" />
                )}
              </div>
            </div>
          </CardHeader>

          {showServicesExpanded && (
            <CardContent className="border-t border-border pt-6">
              {client.services?.length === 0 ? (
                <div className="text-center py-8">
                  <Heart className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No services configured yet.</p>
                  <p className="text-sm text-muted-foreground">Add your first service to start generating reviews.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {client.services?.map((service) => (
                    <div 
                      key={service.id} 
                      className={`flex items-center justify-between p-4 rounded-xl border ${
                        service.is_active !== false ? 'bg-card' : 'bg-muted/30 opacity-60'
                      }`}
                      data-testid={`service-item-${service.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          service.is_active !== false ? 'bg-primary/10' : 'bg-muted'
                        }`}>
                          <Heart className={`w-6 h-6 ${service.is_active !== false ? 'text-primary' : 'text-muted-foreground'}`} />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">
                            {service.service_name || service.name}
                            {service.is_active === false && (
                              <Badge variant="outline" className="ml-2 text-xs">Disabled</Badge>
                            )}
                          </p>
                          {service.outcome_benefit && (
                            <p className="text-sm text-muted-foreground">{service.outcome_benefit}</p>
                          )}
                          {service.staff_name && (
                            <p className="text-xs text-primary mt-1">
                              {service.staff_name}{service.staff_role && ` (${service.staff_role})`}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => startEditService(service)}
                          data-testid={`edit-service-${service.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleService(service.id, service.is_active === false)}
                          data-testid={`toggle-service-${service.id}`}
                        >
                          {service.is_active !== false ? 'Disable' : 'Enable'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          )}
        </Card>
      </main>
    </div>
  );
};
